/**
 * @file App.js
 * @description Composant racine avec gestion du routing
 */

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Moments from './pages/Moments';
import MomentDetail from './pages/MomentDetail';
import Quiz from './pages/Quiz';
import Contact from './pages/Contact';
import Chat from './pages/Chat';
import MeditationCalendar from './pages/MeditationCalendar';
import './index.css';

// ─── Route protégée ───────────────────────────────────────────────────────────
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div className="loader" />
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// ─── Scroll to top on route change ────────────────────────────────────────────
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// ─── Layout avec Navbar ────────────────────────────────────────────────────────
const Layout = ({ children }) => (
  <>
    <Navbar />
    <main style={{ minHeight: '100vh' }}>
      {children}
    </main>
  </>
);

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Layout><Home /></Layout>} />
        <Route path="/login" element={<Layout><Login /></Layout>} />
        <Route path="/register" element={<Layout><Register /></Layout>} />
        <Route path="/quiz" element={<Layout><Quiz /></Layout>} />
        <Route path="/contact" element={<Layout><Contact /></Layout>} />
        <Route path="/chat" element={
          <ProtectedRoute>
            <Layout><Chat /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/moments" element={
          <ProtectedRoute>
            <Layout><Moments /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/moments/:momentId" element={
          <ProtectedRoute>
            <Layout><MomentDetail /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/meditation" element={
          <ProtectedRoute>
            <Layout><MeditationCalendar /></Layout>
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;